/**
 * ComicViewer — the comic reader (reader route). Loads an owned (comicId) or
 * shared (sharedComicId) comic and renders it in BookView.
 */

import React, { useCallback, useEffect, useState } from "react";
import { View, Text, Pressable, StyleSheet, Platform, BackHandler } from "react-native";
import { colors, spacing, borderRadius, textStyle } from "../styles";
import { usePreviewStore } from "../store/previewStore";
import { useSocketStore } from "../store/socketStore";
import { GENERIC_ERROR } from "../store/errorMessages";
import { useSharingStore } from "../store/sharingStore";
import { useConfigStore } from "../store/configStore";
import { useNavigation } from "../navigation/useNavigation";
import BookView from "./book/BookView";
import ShareModal from "./ShareModal";
import { trackView, trackButton, trackComic, View as MetricView, Button, Comic, PageViewedSource } from "../services/metrics";

type Props =
  | { comicId: string; sharedComicId?: undefined }
  | { sharedComicId: string; comicId?: undefined };

export default function ComicViewer(props: Props) {
  const { replace, goBack, canGoBack } = useNavigation();
  const preview = usePreviewStore();
  const [currentPage, setCurrentPage] = useState(0);
  const lastTrackedPage = React.useRef(-1);
  const viewSource = props.sharedComicId ? PageViewedSource.Shared : PageViewedSource.Gallery;
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
    if (page !== lastTrackedPage.current) {
      lastTrackedPage.current = page;
      trackComic(Comic.PageViewed, viewSource, page);
    }
  }, [viewSource]);
  const [shareModalVisible, setShareModalVisible] = useState(false);
  const { shareTargets } = useSharingStore();
  const { getShareTargets, previewSharedComic } = useSocketStore();
  const sharingEnabled = useConfigStore((s) => s.featureFlags.sharing);

  useEffect(() => {
    if (sharingEnabled && props.comicId) getShareTargets();
  }, [sharingEnabled, props.comicId]);

  const handleBack = () => {
    trackButton(MetricView.ComicViewer, Button.ComicViewer.Back);
    if (canGoBack()) {
      goBack();
      return;
    }
    // Cold deep link with nothing to pop: replace the reader entry with the list page.
    replace(props.sharedComicId ? "sharedGallery" : "gallery");
  };

  // Escape to close (web)
  useEffect(() => {
    if (Platform.OS !== "web") return;
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") handleBack(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // Android back button
  useEffect(() => {
    if (Platform.OS === "web") return;
    const sub = BackHandler.addEventListener("hardwareBackPress", () => { handleBack(); return true; });
    return () => sub.remove();
  }, []);

  useEffect(() => {
    setCurrentPage(0);
    preview.clear();
    preview.setLoading(true);

    const { previewESSComic, socket: socketInstance } = useSocketStore.getState();
    const event = props.sharedComicId ? "sharedComicPreview" : "essComicPreview";

    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      preview.setLoading(false);
      if (socketInstance) socketInstance.off(event, done);
      clearTimeout(timeout);
    };
    if (socketInstance) socketInstance.once(event, done);
    const timeout = setTimeout(() => {
      if (settled) return;
      settled = true;
      preview.setLoading(false);
      preview.setError(GENERIC_ERROR);
      if (socketInstance) socketInstance.off(event, done);
    }, 15000);

    if (props.sharedComicId) {
      previewSharedComic(props.sharedComicId);
    } else {
      previewESSComic(props.comicId!);
    }

    return () => {
      settled = true;
      if (socketInstance) socketInstance.off(event, done);
      clearTimeout(timeout);
    };
  }, [props.comicId, props.sharedComicId]);

  useEffect(() => {
    trackView(MetricView.ComicViewer, props.sharedComicId ? "shared" : "owned");
    return () => { usePreviewStore.getState().clear(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Owned / shared comic → BookView reader.
  if (preview.error) {
    return (
      <View style={s.center}>
        <Text style={s.errorText}>{preview.error}</Text>
        <Pressable style={s.btn} onPress={handleBack}>
          <Text style={s.btnText}>Back to Gallery</Text>
        </Pressable>
      </View>
    );
  }

  const pages = preview.pages.map((p: any) => ({
    imageId: p.imageId,
    imageBase64: p.imageBase64,
    imageUrl: p.imageUrl,
    imageError: p.imageError,
    pageNumber: p.pageNumber,
    isCover: p.isCover,
    narrativeText: p.narrativeText,
    audioBase64: p.audioBase64,
    speechMarks: p.speechMarks,
  }));

  return (
    <>
      <BookView
        pages={pages}
        currentPage={currentPage}
        onPageChange={handlePageChange}
        title={preview.title || undefined}
        onBack={handleBack}
        onShare={sharingEnabled && !props.sharedComicId && shareTargets.length > 0 && !preview.isLoading ? () => { trackButton(MetricView.ComicViewer, Button.ComicViewer.Share); setShareModalVisible(true); } : undefined}
        loading={preview.isLoading}
        voiceOver
      />
      {props.comicId && (
        <ShareModal
          visible={shareModalVisible}
          comicId={props.comicId}
          onClose={() => setShareModalVisible(false)}
        />
      )}
    </>
  );
}

const s = StyleSheet.create({
  center: { flex: 1, backgroundColor: colors.bg, alignItems: "center", justifyContent: "center" },
  errorText: { color: colors.danger, fontSize: 16, marginBottom: spacing.md, ...textStyle.body },
  btn: { backgroundColor: colors.surface, paddingVertical: 12, paddingHorizontal: 24, borderRadius: borderRadius.md },
  btnText: { color: colors.text, ...textStyle.heading },
});
